import React from "react";
import {
  AlignLeft,
  Binoculars,
  Code,
  FileText,
  Globe,
  ImageIcon,
  Mic,
  Play,
  ScanText,
  Search,
  StickyNote,
  Table,
  Upload,
} from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { DocumentTypeIconMap, DocumentWithMetadata } from "@/types/knowledge/knowledge-base";

interface DocumentTypeIconProps {
  document: DocumentWithMetadata;
  showTooltip?: boolean;
  showLabel?: boolean;
  iconSize?: "sm" | "md";
}

export const DocumentTypeIcon = ({
  document,
  showTooltip = true,
  showLabel = false,
  iconSize = "md",
}: DocumentTypeIconProps) => {
  const docType = document.source_type || document.metadata?.source;
  if (!docType) return null;

  const iconConfig = DocumentTypeIconMap[docType as keyof typeof DocumentTypeIconMap];
  if (!iconConfig) return null;

  const iconMap: Record<string, React.ComponentType<any>> = {
    Mic,
    Globe,
    Play,
    AlignLeft,
    Code,
    Table,
    FileText,
    Image: ImageIcon,
    Upload,
    Search,
    ScanText,
    Binoculars,
    StickyNote,
  };

  const IconComponent = iconMap[iconConfig.icon as keyof typeof iconMap];
  if (!IconComponent) return null;

  const sizeClass = iconSize === "sm" ? "h-3 w-3" : "h-4 w-4";

  const icon = showLabel ? (
    <div className="flex items-center gap-1">
      <IconComponent className={`${sizeClass} ${iconConfig.color}`} />
      <span>{iconConfig.label}</span>
    </div>
  ) : (
    <IconComponent className={`${sizeClass} ${iconConfig.color}`} />
  );

  if (!showTooltip) {
    return icon;
  }

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>{icon}</TooltipTrigger>
        <TooltipContent>
          <p>{iconConfig.label}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};
